import dayjs from 'dayjs'
import React, { Suspense, useState } from 'react'

import { DownloadIcon, TrashIcon } from '@/components/icon'
import {
  Box,
  Button,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  HStack,
  Separator,
  Skeleton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  Text,
  toaster,
  Tooltip,
} from '@/components/ui'
import {
  generateGraphQLCacheKey,
  useGqlMutation,
  useGqlQuerySuspense,
} from '@/lib/api'
import { useAuthenticatedUser } from '@/lib/auth'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import {
  GetAttachmentsDocument,
  GetInquiryDocument,
  InquiryLogStatus,
} from '@/lib/gql/graphql'
import { formatDate } from '@/utils/datetime'
import { truncateFilename } from '@/utils/filename'

const attachmentDialogInquiryFragment = graphql(`
  fragment AttachmentDialogInquiry on Inquiry {
    inquiryUid
    inquiryNumber
  }
`)

type Props = {
  trigger: React.ReactNode
  inquiry?: FragmentType<typeof attachmentDialogInquiryFragment>
}

export const AttachmentsDialog = ({ trigger, inquiry }: Props) => {
  const inquiryData = getFragmentData(attachmentDialogInquiryFragment, inquiry)
  return (
    <DialogRoot size="xl">
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader p="6" display="flex" flexDirection="column" gap="1">
          <DialogTitle>
            <HStack gap="2">
              <span>問い合わせ資料</span>
              {!!inquiryData && <span>#{inquiryData.inquiryNumber}</span>}
            </HStack>
          </DialogTitle>
        </DialogHeader>
        <Separator />
        <DialogBody px="6" py="3">
          <Box h="320px">
            <TableScrollArea>
              <TableRoot>
                <TableHeader>
                  <TableRow>
                    <TableColumnHeader>ファイル名称</TableColumnHeader>
                    <TableColumnHeader>アップロード日</TableColumnHeader>
                    <TableColumnHeader>操作</TableColumnHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!inquiryData && (
                    <Suspense fallback={<SkeletonRow rowCount={5} />}>
                      <AttachmentsRow inquiryUid={inquiryData.inquiryUid} />
                    </Suspense>
                  )}
                </TableBody>
              </TableRoot>
            </TableScrollArea>
          </Box>
        </DialogBody>
        <Separator />
        <DialogFooter p="6">
          <DialogTrigger asChild>
            <Button>閉じる</Button>
          </DialogTrigger>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
const SkeletonRow = ({ rowCount }: { rowCount: number }) => {
  return [...Array(rowCount)].map((_, index) => (
    <TableRow key={index}>
      <TableCell>
        <Skeleton h="5" />
      </TableCell>
      <TableCell>
        <Skeleton h="5" />
      </TableCell>
      <TableCell>
        <Skeleton h="5" />
      </TableCell>
    </TableRow>
  ))
}

const getAttachmentsQuery = graphql(`
  query GetAttachments($inquiryUid: UUID!) {
    inquiry(inquiryUid: $inquiryUid) {
      latestLog {
        status
      }
      messages(order: PostedAtAsc, includeDeleted: false) {
        postedBy
        attachments {
          inquiryMessageUid
          inquiryMessageAttachmentUid
          deletedAt
          uploadedFile {
            uploadedFileUid
            name
            url(mode: Attachment)
            savedAt
          }
        }
      }
    }
  }
`)
type AttachmentsRowProps = {
  inquiryUid: string
}
const AttachmentsRow = ({ inquiryUid }: AttachmentsRowProps) => {
  const user = useAuthenticatedUser()
  const { data: attachmentsQuery, mutate: mutateAttachments } =
    useGqlQuerySuspense({
      document: getAttachmentsQuery,
      variable: {
        inquiryUid,
      },
    })

  const attachments = attachmentsQuery.inquiry.messages
    .flatMap(message =>
      message.attachments.map(attachment => ({
        ...attachment,
        postedBy: message.postedBy,
      })),
    )
    .filter(attachment => !attachment.deletedAt)

  // 前ファイルをアップロード日の降順でソート
  const sortedAttachments = attachments.toSorted((a, b) => {
    const aDate = dayjs(a.uploadedFile.savedAt)
    const bDate = dayjs(b.uploadedFile.savedAt)
    return aDate.isBefore(bDate) ? 1 : -1
  })

  const isCompleted =
    attachmentsQuery.inquiry.latestLog.status === InquiryLogStatus.Completed

  return sortedAttachments.map(attachment => {
    const isMyAttachment = attachment.postedBy === user.userUid
    const truncatedFilename = truncateFilename(attachment.uploadedFile.name, 25)
    const isTruncated =
      truncatedFilename.length < attachment.uploadedFile.name.length
    return (
      <TableRow key={attachment.inquiryMessageAttachmentUid}>
        <TableCell maxW="320px">
          <Tooltip
            content={attachment.uploadedFile.name}
            disabled={!isTruncated}
          >
            <Text as="span">{truncatedFilename}</Text>
          </Tooltip>
        </TableCell>
        <TableCell>
          {formatDate(attachment.uploadedFile.savedAt, 'YYYY/MM/DD HH:mm')}
        </TableCell>
        <TableCell>
          <HStack gap="1.5">
            <Button
              variant="ghost"
              colorPalette="blue"
              leftIcon={<DownloadIcon />}
              onClick={() => {
                window.open(
                  attachment.uploadedFile.url,
                  '_blank',
                  'noopener,noreferrer',
                )
                toaster.create({
                  type: 'success',
                  description: '1件のファイルをダウンロードしました',
                })
              }}
            >
              ダウンロード
            </Button>
            {isMyAttachment && (
              <DeleteAttachmentDialog
                trigger={
                  <Button
                    variant="ghost"
                    colorPalette="red"
                    leftIcon={<TrashIcon />}
                    disabled={isCompleted}
                  >
                    削除
                  </Button>
                }
                isCompleted={isCompleted}
                inquiryUid={inquiryUid}
                inquiryMessageUid={attachment.inquiryMessageUid}
                inquiryMessageAttachmentUid={
                  attachment.inquiryMessageAttachmentUid
                }
                fileName={attachment.uploadedFile.name}
                mutateAttachments={mutateAttachments}
              />
            )}
          </HStack>
        </TableCell>
      </TableRow>
    )
  })
}

const deleteAttachmentMutation = graphql(`
  mutation DeleteAttachment(
    $inquiryMessageUid: UUID!
    $inquiryMessageAttachmentUid: UUID!
  ) {
    deleteInquiryMessageAttachment(
      input: {
        inquiryMessageUid: $inquiryMessageUid
        inquiryMessageAttachmentUid: $inquiryMessageAttachmentUid
      }
    ) {
      inquiryMessageUid
    }
  }
`)
const DeleteAttachmentDialog = ({
  trigger,
  inquiryUid,
  inquiryMessageUid,
  inquiryMessageAttachmentUid,
  fileName,
  isCompleted,
  mutateAttachments,
}: {
  trigger: React.ReactNode
  inquiryUid: string
  inquiryMessageUid: string
  inquiryMessageAttachmentUid: string
  isCompleted: boolean
  fileName: string
  mutateAttachments: () => void
}) => {
  const [open, setOpen] = useState(false)
  const { request } = useGqlMutation(deleteAttachmentMutation, {
    cacheKeysOnSuccess: [
      generateGraphQLCacheKey(GetInquiryDocument, { inquiryUid }),
      generateGraphQLCacheKey(GetAttachmentsDocument, { inquiryUid }),
    ],
    onSuccess: () => {
      setOpen(false)
      mutateAttachments()
      toaster.create({
        type: 'success',
        title: '1件のファイルを削除しました',
      })
    },
  })
  return (
    <DialogRoot
      size="sm"
      open={open}
      onOpenChange={e => {
        setOpen(e.open)
      }}
    >
      <Tooltip
        disabled={!isCompleted}
        content="完了した問い合わせの添付ファイルは削除できません"
      >
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      </Tooltip>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader p="6" display="flex" flexDirection="column" gap="1">
          <DialogTitle>ファイルを削除する</DialogTitle>
        </DialogHeader>
        <DialogBody px="6" py="3">
          <Text>
            {fileName}を削除しますか？削除後は元に戻すことはできません。
          </Text>
        </DialogBody>
        <DialogFooter p="6">
          <DialogTrigger asChild>
            <Button colorPalette="gray">キャンセル</Button>
          </DialogTrigger>
          <Button
            colorPalette="red"
            onClick={async () => {
              await request({
                inquiryMessageUid,
                inquiryMessageAttachmentUid,
              })
            }}
          >
            削除
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
